import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import styled from 'styled-components';
import { ContentCopy, Share } from '@mui/icons-material';
import { userService } from '../../services/api/user.service';

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing?.md || '16px'};
`;

const CodeContainer = styled(Box)`
  background: ${(props) => props.theme.colors?.input?.background || '#1a1a24'};
  border: 1px solid ${(props) => props.theme.colors?.input?.border || '#333'};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing?.md || '16px'};
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors?.text?.primary || '#fff'};
  word-break: break-all;
  position: relative;

  &:hover {
    border-color: ${(props) => props.theme.colors?.primary || '#007bff'};
  }
`;

const ActionButton = styled(Button)`
  background: ${(props) =>
    props.theme.gradients?.primary ||
    'linear-gradient(135deg, #6D539F 0%, #3B82F6 100%)'};
  color: ${(props) => props.theme.colors?.white || '#fff'};
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  min-height: 48px;

  &:hover {
    background: ${(props) =>
      props.theme.gradients?.primary ||
      'linear-gradient(135deg, #6D539F 0%, #3B82F6 100%)'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px
      ${(props) => props.theme.colors?.primary || '#007bff'}40;
  }

  &:disabled {
    background: ${(props) => props.theme.colors?.text?.secondary || '#999'}40;
    color: ${(props) => props.theme.colors?.text?.secondary || '#999'}80;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled(Button)`
  border: 2px solid ${(props) => props.theme.colors?.input?.border || '#333'};
  color: ${(props) => props.theme.colors?.text?.primary || '#fff'};
  background: transparent;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  min-height: 48px;

  &:hover {
    border-color: ${(props) => props.theme.colors?.primary || '#007bff'};
    background: ${(props) => props.theme.colors?.primary || '#007bff'}10;
    transform: translateY(-1px);
  }
`;

interface ReferralCodeProps {
  referralCode?: string;
  showTitle?: boolean;
  compact?: boolean;
}

export const ReferralCode: React.FC<ReferralCodeProps> = ({
  referralCode = '',
  showTitle = true,
  compact = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  const referralLink = referralCode
    ? userService.getReferralLink(referralCode)
    : '';

  const handleCopyToClipboard = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleShare = async () => {
    if (!referralLink) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Keysha with my referral link',
          text: 'Check out Keysha - the AI-powered task management app!',
          url: referralLink,
        });
        setShared(true);
        setTimeout(() => setShared(false), 3000);
      } catch (err) {
        // User cancelled sharing or sharing failed
        console.log('Sharing cancelled or failed:', err);
      }
    } else {
      // Fallback to copying to clipboard
      handleCopyToClipboard();
    }
  };

  if (!referralCode) {
    return (
      <StyledContainer>
        {showTitle && (
          <Typography variant="h6" color="primary">
            Referral Code
          </Typography>
        )}
        <Alert severity="info">
          Your referral code will appear here once your profile is fully set up.
        </Alert>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      {showTitle && (
        <Typography variant="h6" color="primary">
          Referral Code
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" mb={1}>
        Share your referral link with friends and earn rewards when they sign
        up!
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Your Referral Code
          </Typography>
          <CodeContainer>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <span>{referralCode}</span>
              <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                <IconButton
                  onClick={() => navigator.clipboard.writeText(referralCode)}
                  size="small"
                  sx={{ color: 'inherit' }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CodeContainer>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Your Referral Link
          </Typography>
          <CodeContainer>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <span
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {referralLink}
              </span>
              <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                <IconButton
                  onClick={handleCopyToClipboard}
                  size="small"
                  sx={{ color: 'inherit', flexShrink: 0 }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CodeContainer>
          <Typography
            variant="caption"
            color="text.secondary"
            mt={1}
            display="block"
          >
            Share this link with friends to earn referral rewards
          </Typography>
        </Box>

        <Box display="flex" gap={2} flexDirection={compact ? 'column' : 'row'}>
          <ActionButton
            variant="contained"
            onClick={handleCopyToClipboard}
            startIcon={<ContentCopy />}
            fullWidth={compact}
          >
            Copy Link
          </ActionButton>

          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <SecondaryButton
              variant="outlined"
              onClick={handleShare}
              startIcon={<Share />}
              fullWidth={compact}
            >
              Share
            </SecondaryButton>
          )}
        </Box>

        {copied && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Referral link copied to clipboard!
          </Alert>
        )}

        {shared && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Referral link shared successfully!
          </Alert>
        )}
      </Box>
    </StyledContainer>
  );
};

export default ReferralCode;
